import { useState, useEffect, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Search, Database, Copy, ChevronDown, ChevronUp, ArrowLeft, Hash, Barcode, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { PageHeader } from '@/components/layout/page-header'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { gaeb } from '@/utils/api'
import { cn } from '@/lib/utils'

const formatCurrency = (value: number | null | undefined) => {
  if (value == null || value === 0) return null
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value)
}

const MODES = [
  { key: 'fts', label: 'Volltextsuche', icon: Search, placeholder: 'Suchbegriff eingeben... (z.B. "Kabelrinne 600x60")' },
  { key: 'article', label: 'Artikelnummer', icon: Hash, placeholder: 'Artikelnummer eingeben... (z.B. "7371616")' },
  { key: 'ean', label: 'EAN / GTIN', icon: Barcode, placeholder: 'EAN eingeben... (z.B. "4013339120406")' },
] as const

type SearchMode = (typeof MODES)[number]['key']

const LIMITS = [15, 50, 100] as const

interface DatanormResult {
  id?: number
  articleNumber: string
  shortText1: string
  shortText2?: string
  longText?: string
  unit?: string
  priceBase?: number
  ean?: string
  manufacturerSku?: string
  matchcode?: string
  productGroup?: string
  discountGroup?: string
  metalType?: string
  netPrice?: number
  listPrice?: number
  metalSurcharge?: number
}

interface SearchMeta {
  count: number
  elapsed?: number
  query?: string
  mode?: string
}

function DatanormResultCard({ result, index }: { result: DatanormResult; index: number }) {
  const [expanded, setExpanded] = useState(false)

  const copyToClipboard = useCallback((text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => toast.success(`${label} kopiert`))
  }, [])

  const hasNetPrice = result.netPrice != null && result.netPrice > 0
  const hasListPrice = result.listPrice != null && result.listPrice > 0
  const hasMetal = result.metalSurcharge != null && result.metalSurcharge > 0

  return (
    <div className="rounded-lg border bg-card p-4 transition-colors hover:border-border/80">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs text-muted-foreground">#{index + 1}</span>
            <span
              className="cursor-pointer rounded bg-primary-50 px-2 py-0.5 font-mono text-sm font-bold text-primary-700 hover:bg-primary-100"
              onClick={() => copyToClipboard(result.articleNumber, 'Artikelnummer')}
              title="Artikelnummer kopieren"
            >
              {result.articleNumber}
            </span>
            {hasNetPrice ? (
              <Badge variant="success">Netto: {formatCurrency(result.netPrice)}</Badge>
            ) : hasListPrice ? (
              <Badge variant="warning">Liste: {formatCurrency(result.listPrice)}</Badge>
            ) : (
              <Badge variant="destructive">Kein Preis</Badge>
            )}
            {hasMetal && <Badge variant="warning">ME: {formatCurrency(result.metalSurcharge)}</Badge>}
          </div>
          <p className="mt-1 text-sm font-medium">
            {result.shortText1}
            {result.shortText2 && <span className="text-muted-foreground"> — {result.shortText2}</span>}
          </p>
        </div>
        {result.ean && (
          <button
            onClick={() => copyToClipboard(result.ean!, 'EAN')}
            className="shrink-0 rounded p-1 text-muted-foreground hover:text-foreground"
            title="EAN kopieren"
          >
            <Copy className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground sm:grid-cols-4">
        <div><span className="opacity-60">Einheit:</span> <span className="text-foreground">{result.unit || '-'}</span>
          {result.priceBase && result.priceBase !== 1 && <span className="opacity-60"> (Basis {result.priceBase})</span>}
        </div>
        <div><span className="opacity-60">EAN:</span> <span className="font-mono text-foreground">{result.ean || '-'}</span></div>
        <div><span className="opacity-60">Hersteller:</span> <span className="font-mono text-foreground">{result.manufacturerSku || '-'}</span></div>
        <div><span className="opacity-60">Matchcode:</span> <span className="text-foreground">{result.matchcode || '-'}</span></div>
        {hasListPrice && hasNetPrice && (
          <div><span className="opacity-60">Listenpreis:</span> <span className="text-foreground">{formatCurrency(result.listPrice)}</span></div>
        )}
        <div><span className="opacity-60">Warengruppe:</span> <span className="text-foreground">{result.productGroup || '-'}</span></div>
        <div><span className="opacity-60">Rabattgruppe:</span> <span className="text-foreground">{result.discountGroup || '-'}</span></div>
        {result.metalType && (
          <div><span className="opacity-60">Metallart:</span> <span className="text-foreground">{result.metalType}</span></div>
        )}
      </div>

      {result.longText && (
        <div className="mt-2">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800"
          >
            {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            Langtext {expanded ? 'einklappen' : 'aufklappen'}
          </button>
          {expanded && (
            <div className="mt-1 whitespace-pre-wrap rounded bg-muted p-2 font-mono text-xs leading-relaxed">
              {result.longText}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default function DatanormSearch() {
  const [query, setQuery] = useState('')
  const [mode, setMode] = useState<SearchMode>('fts')
  const [limit, setLimit] = useState<number>(15)
  const [results, setResults] = useState<DatanormResult[]>([])
  const [searchMeta, setSearchMeta] = useState<SearchMeta | null>(null)
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState<{ totalArticles?: number; withNetPrice?: number; withEan?: number } | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    gaeb.getDatanormStatus().then(res => setStatus(res.data)).catch(() => {})
    inputRef.current?.focus()
  }, [])

  const doSearch = useCallback(async () => {
    if (!query.trim()) return
    setLoading(true)
    try {
      const res = await gaeb.searchDatanormAdvanced(query.trim(), mode, limit)
      const data = res.data
      setResults(data.results || [])
      setSearchMeta({ count: data.count, elapsed: data.elapsed, query: data.query, mode: data.mode })
    } catch {
      setResults([])
      setSearchMeta(null)
    } finally {
      setLoading(false)
    }
  }, [query, mode, limit])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') doSearch()
  }

  const currentMode = MODES.find(m => m.key === mode)

  return (
    <div>
      <PageHeader
        title="Datanorm-Katalog"
        description={status ? `${(status.totalArticles || 0).toLocaleString('de-DE')} Artikel` : 'Artikeldatenbank durchsuchen'}
        actions={
          <Link to="/admin/kalkulation">
            <Button variant="outline" size="sm"><ArrowLeft className="h-4 w-4" /> Kalkulation</Button>
          </Link>
        }
      />

      <div className="space-y-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={currentMode?.placeholder}
                  className="w-full rounded-md border border-input bg-white py-2.5 pl-10 pr-4 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                />
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              </div>
              <Button onClick={doSearch} disabled={!query.trim() || loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                Suchen
              </Button>
            </div>

            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-1">
                <span className="mr-1 text-xs text-muted-foreground">Modus:</span>
                {MODES.map(m => (
                  <button
                    key={m.key}
                    onClick={() => setMode(m.key)}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
                      mode === m.key
                        ? 'bg-primary-100 text-primary-800 ring-1 ring-primary-300'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    )}
                  >
                    <m.icon className="h-3 w-3" />
                    {m.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1">
                <span className="mr-1 text-xs text-muted-foreground">Treffer:</span>
                {LIMITS.map(l => (
                  <button
                    key={l}
                    onClick={() => setLimit(l)}
                    className={cn(
                      'rounded px-2 py-1 text-xs font-medium transition-colors',
                      limit === l ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    )}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {searchMeta && !loading && (
          <div className="px-1 text-sm text-muted-foreground">
            <span className="font-medium">{searchMeta.count}</span> Treffer
            {searchMeta.query && <> für <span className="font-mono text-primary-700">"{searchMeta.query}"</span></>}
            {searchMeta.elapsed != null && (
              <span className="ml-1 opacity-60">({searchMeta.elapsed < 1000 ? `${searchMeta.elapsed}ms` : `${(searchMeta.elapsed / 1000).toFixed(2)}s`})</span>
            )}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
        ) : results.length > 0 ? (
          <div className="space-y-2">
            {results.map((r, i) => <DatanormResultCard key={r.id || i} result={r} index={i} />)}
          </div>
        ) : searchMeta ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <Database className="mx-auto mb-3 h-10 w-10 opacity-30" />
              <p className="font-medium">Keine Treffer</p>
              <p className="mt-1 text-sm">Versuchen Sie einen anderen Suchbegriff oder Modus.</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <Search className="mx-auto mb-3 h-10 w-10 opacity-30" />
              <p className="font-medium">Datanorm-Katalog durchsuchen</p>
              <p className="mt-1 text-sm">Geben Sie einen Suchbegriff ein und druecken Sie Enter.</p>
              {status && (
                <div className="mt-4 flex justify-center gap-4 text-xs">
                  <span><strong>{(status.totalArticles || 0).toLocaleString('de-DE')}</strong> Artikel</span>
                  <span><strong>{(status.withNetPrice || 0).toLocaleString('de-DE')}</strong> mit Nettopreis</span>
                  <span><strong>{(status.withEan || 0).toLocaleString('de-DE')}</strong> mit EAN</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
